import React, { useState, useEffect, useContext } from 'react';
import { makeStyles, useTheme } from "@material-ui/core/styles";
import axios from 'axios';


import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Grid,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import EditIcon from '@material-ui/icons/Edit';
import BlockIcon from '@material-ui/icons/Block';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';

import { PageContext } from "./contexts/PageContext";

import PortalHeader from "./views/portal/PortalHeader";
import PortalFields from './views/portal/PortalFields';
import PortalSignatures from './views/portal/PortalSignatures';

import CircularProgressWithLabel from "./views/general/CircularProgressWithLabel";


const useStyles = makeStyles((theme) => ({
  container: {
    marginLeft: "auto",
    marginRight: "auto",
    maxWidth: 1000
  },
  content: {
    marginTop: 100,
    padding: theme.spacing(2),
  },
  progress: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    margin: "auto",
  },
}));



const Portal = ({ credit_id, portal_id, portal_key }) => {
  const theme = useTheme();
  const classes = useStyles();
  const [loading, setLoading] = useState(false);
  const [portal, setPortal] = useState({});
  const [fieldsPercentProgress, setFieldsPercentProgress] = useState(0);
  const [portalPin, setPortalPin] = useState("");
  const [portalAccessToken, setPortalAccessToken] = useState("");
  const [pinDialogOpen, setPinDialogOpen] = useState(false);
  const [pinRequired, setPinRequired] = useState(false);

  const pageContext = useContext(PageContext);

  useEffect(() => {
    let active = true;
    setPortal({});
    setPortalAccessToken("");
    setPortalPin("");
    setPinRequired(false);
    setPinDialogOpen(false);

    if (credit_id && portal_id && portal_key) {
      // The backend accepts an empty PIN only for portals without a stored PIN.
      authenticatePortal("", true, () => active);
    }
    return () => { active = false; };
  }, [credit_id, portal_id, portal_key]);

  useEffect(() => {
    if (!pinRequired) return;

    const lockPortalSession = () => {
      setPortalAccessToken("");
      setPortalPin("");
      setPortal({});
      setPinDialogOpen(true);
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        lockPortalSession();
      }
    };

    const handleBeforeUnload = () => {
      lockPortalSession();
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [pinRequired]);


  const getPortal = (accessToken, isCurrent = () => true) => {
    const options = {
      url: `${process.env.REACT_APP_API_SERVICE_URL}/customerportal/${credit_id}/${portal_id}/${portal_key}`,
      method: 'get',
      headers: {
        "X-Portal-Access-Token": accessToken,
      },
    };
    axios(options)
      .then((res) => {
        if (!isCurrent()) return;
        setPortal({ ...res.data, key: portal_key });
        setPinDialogOpen(false);
      })
      .catch((err) => {
        if (!isCurrent()) return;
        if (err.response && err.response.data && err.response.data.message)
          pageContext.setErrorMessage(err.response.data.message);
        else
          pageContext.setErrorMessage("Das Kundenportal konnte nicht geladen werden.");
        setPortal({});
        const requiresPin = err.response && err.response.status === 401;
        setPinDialogOpen(!!requiresPin);
        if (requiresPin) setPinRequired(true);
        setPortalAccessToken("");
        console.log(err.response && err.response.data ? err.response.data.message : err);
      });
  }

  const authenticatePortal = (pin = portalPin, checkPinRequired = false, isCurrent = () => true) => {
    if (!checkPinRequired && !/^\d{4}$/.test(pin)) {
      pageContext.setErrorMessage("Bitte geben Sie eine 4-stellige numerische PIN ein.");
      return;
    }

    const options = {
      url: `${process.env.REACT_APP_API_SERVICE_URL}/customerportal/auth/${credit_id}/${portal_id}/${portal_key}`,
      method: 'put',
      data: {
        portal_pin: pin,
      },
    };

    axios(options)
      .then((res) => {
        if (!isCurrent()) return;
        const accessToken = res.data.access_token;
        setPortalAccessToken(accessToken);
        setPortalPin("");
        getPortal(accessToken, isCurrent);
      })
      .catch((err) => {
        if (!isCurrent()) return;
        if (checkPinRequired && err.response && err.response.status === 401) {
          setPinRequired(true);
          setPinDialogOpen(true);
          return;
        }
        if (err.response && err.response.data && err.response.data.message)
          pageContext.setErrorMessage(err.response.data.message);
        else
          pageContext.setErrorMessage(checkPinRequired
            ? "Das Kundenportal konnte nicht geladen werden."
            : "Die PIN konnte nicht verifiziert werden.");
      });
  }

  const savePortal = (data, callback = null) => {
    const options = {
      url: `${process.env.REACT_APP_API_SERVICE_URL}/customerportal/${credit_id}/${portal_id}/${portal_key}`,
      method: 'put',
      data: data,
      headers: {
        "X-Portal-Access-Token": portalAccessToken,
      },
    };
    axios(options)
      .then((res) => {
        getPortal(portalAccessToken);
        if (callback)
          callback();
      })
      .catch((err) => {
        console.log(err);
        pageContext.setErrorMessage("Ihre Änderungen konnten nicht gespeichert werden!");
      });
  }

  const getCustomerName = () => {
    if (!portal || !portal.fields_settings)
      return "";
    return portal.fields_settings.find(field => field.meta_key.endsWith('__vorname')).meta_value
      + " "
      + portal.fields_settings.find(field => field.meta_key.endsWith('__nachname')).meta_value
  }


  return (
    <>
      <PortalHeader
        customername={getCustomerName()}
      />
      <Dialog open={pinDialogOpen} disableBackdropClick={true} disableEscapeKeyDown={true}>
        <DialogTitle>Kundenportal PIN</DialogTitle>
        <DialogContent>
          <Typography variant="body2">Bitte geben Sie die 4-stellige PIN ein, die Sie von Ihrem Berater erhalten haben.</Typography>
          <br />
          <TextField
            label="PIN"
            value={portalPin}
            onChange={(e) => {
              const nextPin = (e.target.value || "").replace(/\D/g, "").slice(0, 4);
              setPortalPin(nextPin);
            }}
            inputProps={{ maxLength: 4 }}
            type="password"
          />
        </DialogContent>
        <DialogActions>
          <Button
            color="primary"
            variant="contained"
            onClick={() => {
              authenticatePortal();
            }}
          >
            öffnen
          </Button>
        </DialogActions>
      </Dialog>
      <div className={classes.container}>
        <main>
          {portal && portal.id &&
            <>
              <div className={classes.content}>

                <Typography variant="h3">Hallo {getCustomerName()}</Typography>
                <Typography>In Ihrem Kundenportal finden Sie Eingaben und Dokumente, die Ihr Berater Ihnen zur Verfügung stellt.</Typography>
              </div>

              <Accordion TransitionProps={{ unmountOnExit: false }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Grid container spacing={0} justifyContent="space-between" alignItems="center" >
                    <Grid item>
                      <Typography variant="h5">Persönliche Daten</Typography>
                    </Grid>
                    <Grid item>
                      {portal.status === "open" &&
                        <Chip size="small" icon={<EditIcon />} label="Bitte geben Sie Ihre Daten ein." />
                      }
                      {portal.status === "fields_expired" &&
                        <Chip size="small" icon={<BlockIcon />} label="Der Zugriff ist abgelaufen." />
                      }
                      {portal.status === "fields_done" &&
                        <Chip size="small" icon={<CheckCircleIcon />} color="secondary" label="Sie haben Ihre Eingabe gespeichert." />
                      }
                    </Grid>
                    <Grid item>
                      {portal.status === "open" &&
                        <CircularProgressWithLabel percent={fieldsPercentProgress} />
                      }
                    </Grid>
                  </Grid>
                </AccordionSummary>
                <AccordionDetails>
                  {portal.status === "open"
                    && < Grid container spacing={2}>
                      <PortalFields
                        portal={portal}
                        setFieldsPercentProgress={setFieldsPercentProgress}
                        savePortal={savePortal}
                      />
                    </Grid>
                  }
                  {portal.status === "fields_expired"
                    && <Alert severity="warning">Der Zugriff auf die Eingabefelder ist bereits abgelaufen.</Alert>
                  }
                  {portal.status === "fields_done"
                    && <Alert severity="success">Sie haben alle Eingaben an Ihren Berater geschickt.</Alert>
                  }

                </AccordionDetails>
              </Accordion>
              <Accordion TransitionProps={{ unmountOnExit: true }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography variant="h5">Unterschriften</Typography>
                  {/* {[0, 1, 2, 3].map(index => 
                    <CircularProgressWithLabel percent={creditFunctions.getFieldGroupProgress("person", index + 1)}  key={index}/>
                  )} */}
                </AccordionSummary>
                <AccordionDetails>
                  {(!portal.signatures || portal.signatures.length === 0)
                    ? <Alert severity="info">Es sind momentan keine Dokumente zu unterzeichnen.</Alert>
                    : <PortalSignatures
                      portal={portal}
                      portalAccessToken={portalAccessToken}
                      setPortal={setPortal}
                    />
                  }
                </AccordionDetails>
              </Accordion>
              <Accordion TransitionProps={{ unmountOnExit: true }}>
                <AccordionSummary
                  expandIcon={<ExpandMoreIcon />}
                >
                  <Typography variant="h5">Unterlagenaufbereitung</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Typography variant="body2">Zur beschleunigten Verarbeitung Ihrer Daten, beachten Sie bitte die folgenden Hinweise zur Aufbereitung der Unterlagen:</Typography>
                    </Grid>
                    <Grid item xs={12}>
                      <Typography variant="h6">Ausweis</Typography>
                      <Typography variant="body2" gutterBottom>Vorder- und Hinterseite</Typography>
                      <Typography variant="h6">Kontoauszüge</Typography>
                      <Typography variant="body2" gutterBottom>keine Screenshots, am besten Ausdruck direkt aus dem Onlinebanking</Typography>
                      <Typography variant="h6">Lohnzettel</Typography>
                      <Typography variant="body2" gutterBottom>wenn verschlüsselt, dann Passwort mitübermitteln</Typography>
                      <Typography variant="h6">SV Auszug</Typography>
                      <Typography variant="body2" gutterBottom>inkl. Beitragsgrundlagen</Typography>
                      <Typography variant="h6">Kreditverträge</Typography>
                      <Typography variant="body2" gutterBottom>alle bestehenden Verträge inkl. etwaiger Nachträge</Typography>
                      <Typography variant="h6">Restsaldobestätigungen</Typography>
                      <Typography variant="body2" gutterBottom>inkl. Datum und ersichtlichem IBAN</Typography>

                      {portal && portal.credit_type === "hypothekar" &&
                        <>
                          {/** ab hier nur bei Hypothekarkredit */}
                          <Typography variant="h6">Eigenmittelnachweis</Typography>
                          <Typography variant="body2" gutterBottom>inkl. Datum und ersichtlichem IBAN</Typography>
                          <Typography variant="h6">Pläne</Typography>
                          <Typography variant="body2" gutterBottom>leserliche Pläne aller Geschosse und etwaiger Nebengebäude</Typography>
                          <Typography variant="h6">Fotos</Typography>
                          <Typography variant="body2" gutterBottom>Außenfotos (Garten, Fassade, Einfriedungen, Nebengebäude etc)<br />
                            Innenfotos (jeder Raum der Liegenschaft)</Typography>
                        </>
                      }
                      <br /><br />
                      <Typography variant="body2">Es wird um eine Übermittlung der Dokumente im PDF Format gebeten.</Typography>
                    </Grid>
                  </Grid>
                </AccordionDetails>
              </Accordion>
            </>
          }
        </main>
      </div>
    </>
  );
};

export default Portal;
